// ══════════════════════════════════════════════════════════════════════════════
// PAGE MES NOTES - Style harmonisé avec Liste de tâches
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Plus, Search, FolderOpen, Trash2, Edit3, 
  ChevronRight, Clock, User, MessageCircle, Save, X,
  Briefcase, Users, Heart, Home, Check, Filter, Dumbbell, Sparkles, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════════
// FILTRES PARTICULIER (vie personnelle)
// ═══════════════════════════════════════════════════════════════════════════════
const folderIconsParticulier = {
  'Tous': FolderOpen,
  'Personnel': Home,
  'Famille': Heart,
  'Ami': Users,
  'Travail': Briefcase,
  'Sport': Dumbbell
};

const folderColorsParticulier = {
  'Tous': 'text-gray-600',
  'Personnel': 'text-blue-600',
  'Famille': 'text-pink-500',
  'Ami': 'text-purple-600',
  'Travail': 'text-amber-600',
  'Sport': 'text-green-500'
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILTRES PRO (contexte professionnel)
// ═══════════════════════════════════════════════════════════════════════════════
const folderIconsPro = {
  'Tous': FolderOpen,
  'Prospect': User,
  'Client': Users,
  'Fournisseur': Briefcase,
  'Collaborateur': Users,
  'Chantier': Home,
  'Réunion': Clock
};

const folderColorsPro = {
  'Tous': 'text-gray-600',
  'Prospect': 'text-orange-500',
  'Client': 'text-green-600',
  'Fournisseur': 'text-blue-600',
  'Collaborateur': 'text-purple-600',
  'Chantier': 'text-amber-600',
  'Réunion': 'text-cyan-600'
};

export default function MesNotesPage({ onAskAbra, onBackToHub, isPro = false }) {
  // Sélectionner les filtres selon le contexte
  const folderIcons = isPro ? folderIconsPro : folderIconsParticulier;
  const folderColors = isPro ? folderColorsPro : folderColorsParticulier;
  const defaultFolder = isPro ? 'Prospect' : 'Personnel';
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('Tous');
  const [folderCounts, setFolderCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({ title: '', content: '', folder: defaultFolder });
  const isDarkMode = false;
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showCreateModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showCreateModal, isMobile]);
  
  // Refs et états pour le scroll des filtres
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  // Dossiers dynamiques selon le contexte Pro/Particulier
  const folders = isPro 
    ? ['Tous', 'Prospect', 'Client', 'Fournisseur', 'Collaborateur', 'Chantier', 'Réunion']
    : ['Tous', 'Personnel', 'Famille', 'Ami', 'Travail', 'Sport'];
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const getToken = () => {
    const buyerToken = localStorage.getItem('buyer_token');
    if (buyerToken) return buyerToken;
    const clientToken = localStorage.getItem('client_token');
    if (clientToken) return clientToken;
    const clientUser = localStorage.getItem('clientUser');
    if (clientUser) {
      try {
        const user = JSON.parse(clientUser);
        return user.token;
      } catch (e) {}
    }
    return localStorage.getItem('authToken');
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (activeFolder !== 'Tous') params.append('folder', activeFolder);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/notes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
        setFolderCounts(data.folder_counts || {});
      }
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Erreur de chargement des notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, [activeFolder, searchQuery]);

  const createNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Le contenu est obligatoire');
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newNote)
      });

      if (response.ok) {
        toast.success('Note créée !');
        setShowCreateModal(false);
        setNewNote({ title: '', content: '', folder: 'Personnel' });
        loadNotes();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const updateNote = async () => {
    if (!editingNote?.content?.trim()) {
      toast.error('Le contenu est obligatoire');
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/notes/${editingNote.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingNote.title,
          content: editingNote.content,
          folder: editingNote.folder
        })
      });

      if (response.ok) {
        toast.success('Note mise à jour !');
        setEditingNote(null);
        loadNotes();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Note supprimée');
        loadNotes();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  const totalNotes = folderCounts['Tous'] || notes.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style Liste de tâches */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-[#1e3a5f]'} strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                Mes notes
              </h1>
              {!isMobile && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Retrouve tes idées, notes et informations importantes
                </p>
              )}
            </div>
          </div>
          {isMobile ? (
            <button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-note-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              Nouvelle note
            </button>
          ) : (
            <Button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-note-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
            >
              <Plus size={16} />
              Nouvelle note
            </Button>
          )}
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <FileText size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalNotes} note{totalNotes > 1 ? 's' : ''} sauvegardée{totalNotes > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une note..."
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Filtres par dossier - Style identique à Mes Messages */}
      <div className="flex-shrink-0 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {folders.map((folder) => {
              const count = folderCounts[folder] || 0;
              
              return (
                <button
                  key={folder}
                  onClick={() => setActiveFolder(folder)}
                  data-testid={`folder-${folder.toLowerCase()}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFolder === folder
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {folder}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                        activeFolder === folder 
                          ? 'bg-white/25 text-white' 
                          : 'bg-orange-500 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Flèche orange discrète - parfaitement alignée avec les filtres */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Liste des notes */}
      <div className={`flex-1 rounded-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          /* État vide - Style harmonisé */
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 max-w-sm">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
              }`}>
                <FileText size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-300'} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Aucune note
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {activeFolder === 'Tous' 
                  ? "Crée ta première note ou demande à Abracada de la structurer."
                  : `Aucune note dans "${activeFolder}"`}
              </p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
              >
                <Plus size={16} />
                Nouvelle note
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto h-full">
            {notes.map((note) => {
              const FolderIcon = folderIcons[note.folder] || FolderOpen;
              const folderColor = folderColors[note.folder] || 'text-gray-600';
              
              return (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-4 cursor-pointer transition-colors ${
                    isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`note-item-${note.id}`}
                  onClick={() => setEditingNote(note)}
                >
                  <div className="flex items-start gap-3">
                    {/* Icône dossier */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
                    } ${folderColor}`}>
                      <FolderIcon size={18} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {note.title && (
                            <h3 className={`font-semibold text-sm truncate ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {note.title}
                            </h3>
                          )}
                          <p className={`text-sm line-clamp-2 mt-1 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {note.content}
                          </p>
                          <div className={`flex items-center gap-2 mt-2 text-xs ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            <Clock size={12} />
                            <span>{formatDate(note.created_at)}</span>
                            {note.source === 'message' && (
                              <>
                                <span>·</span>
                                <span className="flex items-center gap-1">
                                  <MessageCircle size={12} />
                                  de {note.sender_name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingNote(note); }}
                            className={`p-2 rounded-lg ${
                              isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Edit3 size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                            className={`p-2 rounded-lg ${
                              isDarkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'
                            }`}
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal création - Version mobile plein écran via Portal */}
      {showCreateModal && isMobile && createPortal(
        <AnimatePresence>
          <motion.div
            key="create-note-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed inset-0 z-[10002] flex flex-col ${isDarkMode ? 'bg-[#0f172a]' : 'bg-white'}`}
          >
            {/* Header - Simple avec flèche retour + titre + safe area */}
            <header 
              className={`flex items-center gap-2 px-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => setShowCreateModal(false)}
                className={`p-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className={`text-[15px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nouvelle note</h1>
            </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                {/* Sélection dossier - Style filtres premium horizontal */}
                <div className="mb-4">
                  <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Dossier
                  </label>
                  <div className="relative flex items-center">
                    <div 
                      className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {folders.filter(f => f !== 'Tous').map((folder) => {
                        const Icon = folderIcons[folder];
                        const isSelected = newNote.folder === folder;
                        
                        return (
                          <button
                            key={folder}
                            type="button"
                            onClick={() => setNewNote({ ...newNote, folder })}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                              isSelected
                                ? 'bg-orange-500 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200'
                            }`}
                          >
                            <Icon size={14} />
                            {folder}
                          </button>
                        );
                      })}
                    </div>
                    {/* Flèche orange pour indiquer le scroll */}
                    <div className="flex-shrink-0 flex items-center pointer-events-none">
                      <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                {/* Titre */}
                <div className="mb-4">
                  <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Titre (optionnel)
                  </label>
                  <input
                    type="text"
                    value={newNote.title}
                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                    placeholder="Titre de la note..."
                    className={`w-full p-4 rounded-xl border text-[16px] ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Contenu */}
                <div className="mb-4">
                  <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Contenu *
                  </label>
                  <textarea
                    value={newNote.content}
                    onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                    placeholder="Écris ta note ici..."
                    rows={6}
                    className={`w-full p-4 rounded-xl border text-[15px] resize-none ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>

              {/* Footer fixe - Se positionne au-dessus du clavier */}
              <div 
                className={`p-4 border-t transition-all duration-200 ${
                  isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-white'
                }`}
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                {/* Bouton Créer avec Abracada */}
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ title: newNote.title, content: newNote.content, folder: newNote.folder, isNew: true });
                      setShowCreateModal(false);
                    } else {
                      toast.info('Demande à Abra de t\'aider à structurer ta note !');
                    }
                  }}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all ${
                    isDarkMode 
                      ? 'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30' 
                      : 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                  }`}
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                {/* Boutons Annuler et Créer */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className={`flex-1 h-12 rounded-xl font-semibold transition-all border ${
                      isDarkMode 
                        ? 'border-slate-600 text-gray-300 hover:bg-slate-800' 
                        : 'border-gray-300 text-[#1e3a5f] hover:bg-gray-100'
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createNote}
                    disabled={!newNote.content.trim()}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
        document.body
      )}

      {/* Modal création - Version desktop centré */}
      <AnimatePresence>
        {showCreateModal && !isMobile && (
            /* ═══ VERSION DESKTOP - Modal centré ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Nouvelle note</h3>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  {/* Sélection dossier */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dossier</label>
                    <div className="flex flex-wrap gap-2">
                      {folders.filter(f => f !== 'Tous').map((folder) => {
                        const Icon = folderIcons[folder];
                        const isSelected = newNote.folder === folder;
                        
                        return (
                          <button
                            key={folder}
                            type="button"
                            onClick={() => setNewNote({ ...newNote, folder })}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all border-2 ${
                              isSelected 
                                ? 'bg-orange-50 border-orange-500 text-orange-600' 
                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            <Icon size={14} />
                            {folder}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Titre */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre (optionnel)</label>
                    <input
                      type="text"
                      value={newNote.title}
                      onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                      placeholder="Titre de la note..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Contenu */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contenu *</label>
                    <textarea
                      value={newNote.content}
                      onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                      placeholder="Écris ta note ici..."
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ title: newNote.title, content: newNote.content, folder: newNote.folder, isNew: true });
                          setShowCreateModal(false);
                        } else {
                          toast.info('Demande à Abra de t\'aider à structurer ta note !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={createNote}
                      disabled={!newNote.content.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Créer la note
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>

      {/* Modal édition - Style harmonisé avec Nouvelle tâche */}
      <AnimatePresence>
        {editingNote && (
          <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Modifier la note</h3>
                <button onClick={() => setEditingNote(null)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Sélection dossier */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dossier</label>
                  <div className="flex flex-wrap gap-2">
                    {folders.filter(f => f !== 'Tous').map((folder) => {
                      const Icon = folderIcons[folder];
                      const isSelected = editingNote.folder === folder;
                      
                      return (
                        <button
                          key={folder}
                          type="button"
                          onClick={() => setEditingNote({ ...editingNote, folder })}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all border-2 ${
                            isSelected 
                              ? 'bg-orange-50 border-orange-500 text-orange-600' 
                              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          <Icon size={14} />
                          {folder}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Titre */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre (optionnel)</label>
                  <input
                    type="text"
                    value={editingNote.title || ''}
                    onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                    placeholder="Titre de la note..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* Contenu */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contenu *</label>
                  <textarea
                    value={editingNote.content || ''}
                    onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                    placeholder="Écris ta note ici..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditingNote(null)}>
                    Annuler
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      if (onAskAbra) {
                        onAskAbra({ title: editingNote.title, content: editingNote.content, folder: editingNote.folder, isNew: false });
                        setEditingNote(null);
                      } else {
                        toast.info('Demande à Abra de t\'aider à améliorer ta note !');
                      }
                    }}
                    className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                    style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                  >
                    <Sparkles size={16} />
                    Demander à Abra
                  </button>
                  <Button
                    onClick={updateNote}
                    disabled={!editingNote.content?.trim()}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Enregistrer
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
